import { Box } from '@chakra-ui/react';
import _ from 'lodash';
import React, { FC } from 'react';

import { TextField } from 'Containers/App/types';
import { MemberCard, ProfileAudio, CapacityToPayCard } from 'ContentfulDefaults/types/profile';
import { ContentfulMedicareInfo } from 'ContentfulDefaults/types/profileAddtl';
import { usePremiumContext } from 'Contexts/premiumContext';
import { useTextContext } from 'Contexts/textContext';
import { FadeUp, Icon, Tooltip } from 'DesignLibrary/atoms/';
import { AudioTooltip } from 'DesignLibrary/atoms/AudioTooltip';
import { H6 } from 'DesignLibrary/atoms/typography';
import InfoBar from 'DesignLibrary/molecules/InfoBar';
import RichText from 'Shared/RichText';
import { applyTextReplacements, PREMIUM_TEXT_REPLACEMENT } from 'Utils/textReplacement';

import { QuestionContainer, QuestionContent, SideTab } from './styled';

type ProfileCardNames =
  | 'enrollment'
  | 'claims'
  | 'eligibility'
  | 'location'
  | 'member'
  | 'pregnancy'
  | 'risk'
  | 'income'
  | 'capacity_to_pay'
  | 'incentives'
  | 'provider_preferences'
  | 'medicareInfo'
  | 'hsa_funds';

// Most cards live on the Profile Section content type, but it is full, so newer ones
// are added to Profile Section Addtl instead.
const ADDTL_CARD_PATHS: Partial<Record<ProfileCardNames, string>> = {
  provider_preferences: 'profile_section_addtl.provider_preferences',
};

export interface QuestionProps {
  error?: boolean;
  isIncomplete?: boolean;
  name: ProfileCardNames;
  audioTooltipsEnabled?: boolean;
  audioTooltipContentfulPath?: TextField;
  children?: React.ReactNode;
  isMedicareCallout?: boolean;
}

const Question: FC<QuestionProps> = ({
  error = false,
  name,
  children,
  isIncomplete,
  audioTooltipsEnabled = false,
  audioTooltipContentfulPath,
  isMedicareCallout = false,
}) => {
  const { retrieveContentfulData, locale } = useTextContext();
  const { useContributionTerm } = usePremiumContext();

  const shouldUseAudioTooltip: boolean = !!(audioTooltipsEnabled && audioTooltipContentfulPath);

  const audioConfig = retrieveContentfulData<ProfileAudio>(audioTooltipContentfulPath, {} as ProfileAudio);

  const profileCard = isMedicareCallout
    ? retrieveContentfulData<ContentfulMedicareInfo>('profile_section_addtl.medicare_info_card')
    : name === 'hsa_funds'
      ? retrieveContentfulData<CapacityToPayCard>('profile_section.capacity_to_pay')
      : retrieveContentfulData<MemberCard>(
          (ADDTL_CARD_PATHS[name] || `profile_section.${name}`) as TextField,
        );

  let cardName: string;
  let tooltipLabel: any;
  let customMessage: any | undefined;

  if (isMedicareCallout) {
    const medicareCard = profileCard as ContentfulMedicareInfo;
    cardName = medicareCard?.profile_callout_name || 'Medicare';
    tooltipLabel = medicareCard?.profile_callout_tooltips?.name || { text: '' };
  } else if (name === 'hsa_funds') {
    const hsaCard = profileCard as CapacityToPayCard;
    cardName = hsaCard?.hsa_header || 'HSA Funds';
    tooltipLabel = hsaCard?.hsa_tooltip?.name || { text: '' };
    customMessage = hsaCard?.custom_message;
  } else {
    const memberCard = profileCard as MemberCard;
    cardName = memberCard?.name || 'Profile Card';
    tooltipLabel = memberCard?.tooltips?.name || { text: '' };
    customMessage = memberCard?.custom_message;
  }

  const tooltipId = `tooltip-${_.kebabCase(cardName)}`;
  const audioTooltipId = `audio-${tooltipId}`;

  if (useContributionTerm) {
    tooltipLabel.text = applyTextReplacements(tooltipLabel.text, PREMIUM_TEXT_REPLACEMENT[locale]);
  }

  return (
    <FadeUp isOpen>
      <QuestionContainer error={error} id={name} data-testid="question-card">
        {shouldUseAudioTooltip && (
          <Box pos="absolute" top={4} left={['initial', -5]} right={[4, 'initial']}>
            <AudioTooltip
              data-testid={audioTooltipId}
              urlToAudio={audioConfig.audio_file_url}
              urlToClosedCaptionVtt={audioConfig.vtt_transcript_file_url}
            />
          </Box>
        )}

        <SideTab incomplete={isIncomplete} error={error}>
          <div className="title">
            <H6 as="h2" color="white">
              {cardName}
            </H6>
          </div>

          {!shouldUseAudioTooltip && tooltipLabel?.text && (
            <Tooltip id={tooltipId} data-testid={tooltipId} label={tooltipLabel}>
              <div className="icon">
                <Icon type="Question" color="--text-black" />
              </div>
            </Tooltip>
          )}
        </SideTab>
        <QuestionContent>
          {customMessage && (
            <InfoBar
              className="client-message"
              text={
                <RichText
                  inlineParagraphs
                  field={
                    name === 'hsa_funds'
                      ? ('profile_section.capacity_to_pay.custom_message.text' as TextField)
                      : (`profile_section.${name}.custom_message.text` as TextField)
                  }
                />
              }
              color={customMessage.color}
              header={customMessage.header}
              stretch
              icon={customMessage.icon}
              buttons={[{ text: customMessage.url_button_text, url: customMessage.url }]}
            />
          )}
          {children}
        </QuestionContent>
      </QuestionContainer>
    </FadeUp>
  );
};

export default Question;
